/**
 * Lab Page component
 * Main two-panel interface for video analysis and app generation
 * Context: Core workspace where users interact with videos and create content
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Container, 
  Grid, 
  Stack, 
  TextInput, 
  Button, 
  Title,
  Text,
  Loader
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

import { useProjectStore } from '../context/useProjectStore';
import { isValidYouTubeUrl } from '../lib/utils';

// Import actual components
import VideoPlayer from '../components/lab/VideoPlayer';
import ActionToolbar from '../components/lab/ActionToolbar';
import OutputCanvas from '../components/lab/OutputCanvas';
import type { AnalysisResult, Generation } from '../lib/types';

export default function LabPage() {
  const { projectId } = useParams();
  const [videoUrl, setVideoUrl] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [generatedApp, setGeneratedApp] = useState<Generation | null>(null);
  const [requestedTimecode, setRequestedTimecode] = useState<number | null>(null);
  
  const { 
    currentProject, 
    isLoading, 
    createProject,
  } = useProjectStore();

  useEffect(() => {
    setIsValidUrl(isValidYouTubeUrl(videoUrl));
  }, [videoUrl]);

  useEffect(() => {
    if (projectId) {
      // Load existing project
      // This would fetch the project by ID in a real implementation
      console.log('Loading project:', projectId);
    }
  }, [projectId]);

  const handleCreateProject = async () => {
    if (!isValidUrl) return;
    
    try {
      await createProject(videoUrl);
      setVideoUrl(''); // Clear URL after project creation
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  const handleActionStart = () => {
    console.log('Action started');
  };

  const handleActionComplete = (result: AnalysisResult | Generation) => {
    if ('mode' in result) {
      // It's an AnalysisResult
      setAnalysisResults(prev => [...prev, result]);
    } else {
      // It's a Generation
      setGeneratedApp(result);
    }
  };

  const handleTimestampClick = (time: number) => {
    setRequestedTimecode(time);
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text>Setting up your Video Lab...</Text>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      {!currentProject ? (
        // Project creation form
        <Stack align="center" gap="xl" py={80}>
          <Title order={1} ta="center">
            Start Your Video Lab Project
          </Title>
          <Text color="dimmed" ta="center" size="lg" maw={600}>
            Paste any YouTube URL to begin analyzing and transforming it into interactive learning content
          </Text>
          
          <Stack w="100%" maw={500}>
            <TextInput
              placeholder="https://youtube.com/watch?v=..."
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.currentTarget.value)}
              size="lg"
              leftSection={<IconSearch size={16} />}
              error={videoUrl && !isValidUrl ? 'Please enter a valid YouTube URL' : null}
            />
            
            <Button 
              size="lg"
              disabled={!isValidUrl}
              onClick={handleCreateProject}
              loading={isLoading}
            >
              Create Project
            </Button>
          </Stack>
        </Stack>
      ) : (
        // Main lab interface
        <Grid gutter="md">
          {/* Left Panel - Video Player + Actions */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="md">
              <VideoPlayer 
                url={currentProject.videoMetadata.url}
                timecodeList={analysisResults
                  .filter(r => Array.isArray(r.resultData) && r.resultData[0]?.time)
                  .flatMap(r => r.resultData as any)
                  .slice(0, 20) // Limit markers for performance
                }
                requestedTimecode={requestedTimecode}
                jumpToTimecode={handleTimestampClick}
              />
              <ActionToolbar 
                videoUrl={currentProject.videoMetadata.url}
                onActionStart={handleActionStart}
                onActionComplete={handleActionComplete}
              />
            </Stack>
          </Grid.Col>
          
          {/* Right Panel - Output Canvas */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <OutputCanvas 
              analysisResults={analysisResults}
              generatedApp={generatedApp}
              project={currentProject}
              onTimestampClick={handleTimestampClick}
            />
          </Grid.Col>
        </Grid>
      )}
    </Container>
  );
}